/**
 * Selector propio del panel.
 *
 * Sustituye al `<select>` nativo, cuya lista desplegable la dibuja el sistema
 * operativo y no se puede vestir con el diseño del panel (aparecía gris, con la
 * tipografía y el resaltado del navegador).
 *
 * La API es **compatible con la del `<select>`** para que la sustitución sea
 * directa: se pasan `<option>` como hijos y `onChange` recibe un evento con
 * `target.value`, igual que antes.
 *
 *   <Select value={estado} onChange={(e) => setEstado(e.target.value)}>
 *     <option value="">Todos</option>
 *     <option value="1">Activos</option>
 *   </Select>
 *
 * Detalles de comportamiento:
 * - La lista se dibuja en un **portal** anclado al disparador, así no la recorta
 *   la tarjeta ni el contenedor con desplazamiento de la tabla.
 * - Se navega con las flechas, `Inicio`/`Fin`, `Enter`/`Espacio` y `Escape`, y
 *   se cierra al hacer clic fuera, al desplazarse o al cambiar el tamaño.
 * - Los `<option disabled>` se muestran deshabilitados, como en el nativo.
 * - Los `<optgroup label="...">` se aceptan y se dibujan como **encabezados de
 *   grupo** dentro de la lista (el título no es elegible y las flechas lo saltan).
 */

import { Check, ChevronDown, Search } from 'lucide-react';
import {
  Children,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/lib/utils';
import { posicionarFlotante } from '@/lib/flotante';

/** Opción del selector. */
interface Opcion {
  valor: string;
  etiqueta: string;
  deshabilitada: boolean;
  /** Grupo del `<optgroup>` al que pertenece (si viene agrupada). */
  grupo?: string;
  /** Texto extra para el buscador (p. ej. el RUC/DNI, aunque no esté en la etiqueta). */
  busqueda?: string;
}

export interface SelectProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'value' | 'onChange' | 'children'> {
  value?: string | number | readonly string[];
  /** Igual que el `<select>` nativo: recibe un evento con `target.value`. */
  onChange?: (evento: { target: { value: string } }) => void;
  /** `<option>` del selector. */
  children?: ReactNode;
  /** Texto cuando no hay valor (por defecto, el de la primera opción). */
  placeholder?: string;
  /** Muestra un buscador dentro de la lista (para listas largas). */
  searchable?: boolean;
}

/** Texto plano de un nodo (las etiquetas de las opciones son texto). */
function textoPlano(nodo: ReactNode): string {
  if (nodo === null || nodo === undefined || typeof nodo === 'boolean') return '';
  if (typeof nodo === 'string' || typeof nodo === 'number') return String(nodo);

  if (Array.isArray(nodo)) return nodo.map(textoPlano).join('');

  if (isValidElement(nodo)) {
    const props = nodo.props as { children?: ReactNode };

    return textoPlano(props.children);
  }

  return '';
}

/** Lee las `<option>` de los hijos (y de los `<optgroup>`), como haría el navegador. */
function leerOpciones(children: ReactNode): Opcion[] {
  return Children.toArray(children).flatMap((hijo): Opcion[] => {
    if (!isValidElement(hijo)) return [];

    if (hijo.type === 'optgroup') {
      const grupo = hijo.props as { label?: string; children?: ReactNode };

      return leerOpciones(grupo.children).map((opcion) => ({
        ...opcion,
        grupo: grupo.label,
      }));
    }

    if (hijo.type !== 'option') return [];

    const props = hijo.props as {
      value?: string | number;
      disabled?: boolean;
      'data-search'?: string;
      children?: ReactNode;
    };

    return [
      {
        valor: String(props.value ?? textoPlano(props.children)),
        etiqueta: textoPlano(props.children),
        deshabilitada: Boolean(props.disabled),
        busqueda: props['data-search'] ?? '',
      },
    ];
  });
}

const ALTO_MAXIMO = 288;

export function Select({
  value,
  onChange,
  children,
  className,
  placeholder,
  disabled,
  searchable,
  id,
  ...rest
}: SelectProps) {
  const autoId = useId();
  const selectId = id ?? `select-${autoId}`;
  const opciones = useMemo(() => leerOpciones(children), [children]);

  const valorActual = value === undefined || value === null ? '' : String(value);
  const indiceElegido = opciones.findIndex((opcion) => opcion.valor === valorActual);
  const elegida = indiceElegido >= 0 ? opciones[indiceElegido] : null;

  // Sin valor se muestra el texto de la primera opción si es el marcador vacío
  // (`<option value="">Todos</option>`), como hace el `<select>` nativo.
  const etiqueta =
    elegida?.etiqueta || (valorActual === '' ? opciones[0]?.etiqueta ?? '' : valorActual);
  const etiquetaVisible = etiqueta || placeholder || '';

  const [abierto, setAbierto] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [posicion, setPosicion] = useState<{ top: number; left: number; ancho: number } | null>(null);
  // Los encabezados de grupo no se eligen con las flechas: cuentan para el alto
  // de la lista, pero no para la navegación.
  const grupos = useMemo(
    () => new Set(opciones.map((opcion) => opcion.grupo).filter(Boolean)).size,
    [opciones],
  );
  const [activo, setActivo] = useState(indiceElegido);
  const disparador = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const buscador = useRef<HTMLInputElement>(null);

  // Opciones visibles: con búsqueda se filtran por su etiqueta.
  const opcionesVisibles = useMemo(() => {
    if (!searchable) return opciones;

    const termino = busqueda.trim().toLowerCase();

    if (!termino) return opciones;

    return opciones.filter(
      (opcion) =>
        opcion.etiqueta.toLowerCase().includes(termino) ||
        (opcion.busqueda ? opcion.busqueda.toLowerCase().includes(termino) : false),
    );
  }, [opciones, busqueda, searchable]);

  const cerrar = useCallback(() => {
    setAbierto(false);
    setBusqueda('');
    disparador.current?.focus();
  }, []);

  const abrir = useCallback(() => {
    const caja = disparador.current?.getBoundingClientRect();

    if (!caja) return;

    const ancho = Math.max(180, caja.width);
    // Alto estimado de la lista (cada opción ronda los 38 px y cada grupo suma
    // el encabezado).
    const alto = Math.min(ALTO_MAXIMO, opciones.length * 38 + grupos * 26 + 8);
    const { top, left } = posicionarFlotante(caja, alto, ancho);

    setPosicion({ top, left, ancho });
    setBusqueda('');
    setActivo(searchable ? 0 : indiceElegido >= 0 ? indiceElegido : 0);
    setAbierto(true);
  }, [grupos, indiceElegido, opciones.length, searchable]);

  // Al abrir un selector con búsqueda, el foco va al buscador.
  useEffect(() => {
    if (abierto && searchable) {
      buscador.current?.focus();
    }
  }, [abierto, searchable]);

  // Cierre al hacer clic fuera, desplazarse o redimensionar.
  useEffect(() => {
    if (!abierto) return undefined;

    const alClicFuera = (evento: MouseEvent) => {
      const destino = evento.target as Node;

      if (disparador.current?.contains(destino)) return;
      if (panel.current?.contains(destino)) return;

      setAbierto(false);
    };

    // Desplazarse **dentro** de la lista (rueda del ratón) no la cierra; sólo lo
    // hace el desplazamiento de la página o de la tabla que la contiene.
    const alDesplazar = (evento: Event) => {
      const destino = evento.target as Node | null;

      if (destino && panel.current?.contains(destino)) return;

      setAbierto(false);
    };

    document.addEventListener('mousedown', alClicFuera);
    window.addEventListener('scroll', alDesplazar, true);
    window.addEventListener('resize', alDesplazar);

    return () => {
      document.removeEventListener('mousedown', alClicFuera);
      window.removeEventListener('scroll', alDesplazar, true);
      window.removeEventListener('resize', alDesplazar);
    };
  }, [abierto, selectId]);

  const elegir = (opcion: Opcion) => {
    if (opcion.deshabilitada) return;

    onChange?.({ target: { value: opcion.valor } });
    cerrar();
  };

  /** Flechas, Inicio/Fin, Enter y Escape (con la lista abierta o cerrada). */
  const alTeclear = (evento: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;

    const abrirCon = (indice: number) => {
      if (!abierto) {
        abrir();

        return;
      }

      setActivo(Math.max(0, Math.min(indice, opcionesVisibles.length - 1)));
    };

    switch (evento.key) {
      case 'ArrowDown':
        evento.preventDefault();
        abrirCon(abierto ? activo + 1 : 0);
        break;
      case 'ArrowUp':
        evento.preventDefault();
        abrirCon(abierto ? activo - 1 : 0);
        break;
      case 'Home':
        if (abierto) {
          evento.preventDefault();
          setActivo(0);
        }
        break;
      case 'End':
        if (abierto) {
          evento.preventDefault();
          setActivo(opcionesVisibles.length - 1);
        }
        break;
      case 'Enter':
      case ' ':
        evento.preventDefault();

        if (abierto && opcionesVisibles[activo]) elegir(opcionesVisibles[activo]);
        else abrir();
        break;
      case 'Escape':
        if (abierto) {
          evento.preventDefault();
          cerrar();
        }
        break;
      default:
        break;
    }
  };

  return (
    <>
      <button
        {...rest}
        ref={disparador}
        id={selectId}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={abierto}
        aria-controls={`${selectId}-lista`}
        disabled={disabled ?? false}
        className={cn(
          'flex items-center justify-between gap-2 rounded-control border border-ink-200 bg-white',
          'px-3 text-left text-sm text-ink-900 transition-colors',
          'focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none',
          'disabled:cursor-not-allowed disabled:bg-ink-50 disabled:opacity-70',
          className ?? 'h-11 w-full',
        )}
        onClick={() => (abierto ? cerrar() : abrir())}
        onKeyDown={alTeclear}
      >
        <span className={cn('truncate', !etiquetaVisible && 'text-ink-400')} title={etiquetaVisible}>
          {etiquetaVisible}
        </span>
        <ChevronDown
          className={cn('size-4 shrink-0 text-ink-400 transition-transform', abierto && 'rotate-180')}
          aria-hidden="true"
        />
      </button>

      {abierto && posicion
        ? createPortal(
            <div
              id={`${selectId}-lista`}
              ref={panel}
              role="listbox"
              tabIndex={-1}
              aria-labelledby={selectId}
              style={{
                top: posicion.top,
                left: posicion.left,
                width: posicion.ancho,
                maxHeight: ALTO_MAXIMO,
              }}
              className="fixed z-50 overflow-auto rounded-card border border-ink-200 bg-white p-1 shadow-xl"
            >
              {searchable ? (
                <div className="sticky top-0 z-10 mb-1 border-b border-ink-100 bg-white px-1 pt-1 pb-1.5">
                  <div className="relative">
                    <Search
                      className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-ink-400"
                      aria-hidden="true"
                    />
                    <input
                      ref={buscador}
                      type="text"
                      role="searchbox"
                      autoComplete="off"
                      placeholder="Buscar…"
                      value={busqueda}
                      onChange={(evento) => {
                        setBusqueda(evento.target.value);
                        setActivo(0);
                      }}
                      className="h-8 w-full rounded-control border border-ink-200 bg-white pr-2 pl-8 text-sm text-ink-900 transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none"
                    />
                  </div>
                </div>
              ) : null}

              {opcionesVisibles.length === 0 ? (
                <p className="px-2.5 py-6 text-center text-sm text-ink-400">
                  Sin resultados para «{busqueda.trim()}».
                </p>
              ) : null}

              {opcionesVisibles.map((opcion, indice) => {
                const seleccionada = opcion.valor === valorActual;
                const resaltada = indice === activo;
                const grupo = opcion.grupo && opcionesVisibles[indice - 1]?.grupo !== opcion.grupo;

                return (
                  <div key={`${opcion.valor}-${indice}`}>
                    {grupo ? (
                      <p
                        role="presentation"
                        className="px-2.5 pt-2 pb-1 text-[10px] font-semibold tracking-wide text-ink-400 uppercase"
                      >
                        {opcion.grupo}
                      </p>
                    ) : null}
                    <button
                      type="button"
                      role="option"
                      aria-selected={seleccionada}
                      disabled={opcion.deshabilitada}
                      className={cn(
                        'flex w-full items-center justify-between gap-2 rounded-control px-2.5 py-2 text-left text-sm',
                        opcion.deshabilitada
                          ? 'cursor-not-allowed text-ink-300'
                          : resaltada
                            ? 'bg-brand-50 text-brand-800'
                            : 'text-ink-700',
                        !opcion.deshabilitada && !resaltada && 'hover:bg-ink-50',
                      )}
                      onMouseEnter={() => setActivo(indice)}
                      onClick={() => elegir(opcion)}
                    >
                      <span className="truncate" title={opcion.etiqueta}>
                        {opcion.etiqueta}
                      </span>
                      {seleccionada ? (
                        <Check className="size-4 shrink-0 text-brand-600" aria-hidden="true" />
                      ) : null}
                    </button>
                  </div>
                );
              })}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
